import { SoilBoard, SoilBoardCell } from '../../services/soilProgramService';
import { currentMonthKey } from '../../utils/monthKey';

export interface SoilBoardKpi {
  sessionsInFlight: number;
  sessionsPlanned: number;
  sessionsInProgress: number;
  groupsAwaitingSignoff: number;
  oldestAwaitingDays: number | null;
  appliedThisMonthKg: number;
  plannedThisMonthKg: number;
  unassignedOrchards: number;
  draftSessions: number;
}

export interface SoilBoardAttention {
  awaitingSignoff: { sessionId: string; productName: string; month: string; count: number }[];
  draftSessions: { sessionId: string; productName: string; month: string; unassignedOrchards: number }[];
  uncreated: { month: string; productCount: number }[];
}

const DAY_MS = 86_400_000;

// Moved to utils/monthKey so soil and foliar share ONE definition. Re-exported
// here because soilBoardSummary.test.ts and this module both import it by name.
export { currentMonthKey } from '../../utils/monthKey';

export function summariseBoard(
  board: SoilBoard,
  now: Date = new Date(),
): { kpi: SoilBoardKpi; attention: SoilBoardAttention } {
  const cells: SoilBoardCell[] = board.cells || [];
  const withSession = cells.filter((c) => c.session !== null);
  const thisMonth = currentMonthKey(now);
  const monthCells = cells.filter((c) => c.month === thisMonth);

  const awaitingCells = withSession.filter((c) => (c.session!.groupAwaitingSignoff || 0) > 0);
  const draftCells = withSession.filter((c) => c.session!.status === 'draft');

  const oldestIso = awaitingCells
    .map((c) => c.session!.oldestAwaitingCompletedAt)
    .filter((v): v is string => !!v)
    .sort()[0];

  const kpi: SoilBoardKpi = {
    sessionsInFlight: withSession.filter((c) => c.session!.status === 'planned' || c.session!.status === 'in_progress').length,
    sessionsPlanned: withSession.filter((c) => c.session!.status === 'planned').length,
    sessionsInProgress: withSession.filter((c) => c.session!.status === 'in_progress').length,
    groupsAwaitingSignoff: withSession.reduce((s, c) => s + (c.session!.groupAwaitingSignoff || 0), 0),
    oldestAwaitingDays: oldestIso
      ? Math.max(0, Math.floor((now.getTime() - new Date(oldestIso).getTime()) / DAY_MS))
      : null,
    appliedThisMonthKg: monthCells.reduce((s, c) => s + (c.session?.appliedKg || 0), 0),
    plannedThisMonthKg: monthCells.reduce((s, c) => s + c.plannedKg, 0),
    unassignedOrchards: draftCells.reduce((s, c) => s + (c.session!.unassignedOrchards || 0), 0),
    draftSessions: draftCells.length,
  };

  // A month is "uncreated" only when it has planned pairs and NONE of them has a
  // session yet — a partially created month is already visible on the board.
  const uncreated = (board.months || [])
    .map((month) => {
      const inMonth = cells.filter((c) => c.month === month);
      const missing = inMonth.filter((c) => c.session === null);
      return inMonth.length > 0 && missing.length === inMonth.length
        ? { month, productCount: missing.length }
        : null;
    })
    .filter((v): v is { month: string; productCount: number } => v !== null);

  return {
    kpi,
    attention: {
      awaitingSignoff: awaitingCells.map((c) => ({
        sessionId: c.session!._id, productName: c.productName, month: c.month,
        count: c.session!.groupAwaitingSignoff,
      })),
      draftSessions: draftCells.map((c) => ({
        sessionId: c.session!._id, productName: c.productName, month: c.month,
        unassignedOrchards: c.session!.unassignedOrchards,
      })),
      uncreated,
    },
  };
}
